import {useEffect, useRef} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon, EditIcon, MapMarkerOutlineIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Highlight, Text} from "@pg-design/text-module";

import type {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {
    hitGtmHomeMatchBasicFlowEditPreferences,
    hitGtmHomeMatchBasicFlowModal,
    hitGtmHomeMatchBasicFlowToggle
} from "../../tracking/home_match_basic_flow_gtm_events";
import type {HomeMatchBasicPreferencesFlowSource, IHomeMatchBasicPreferencesFlowTrackingContext} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import type {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {
    getHomeMatchBasicBudgetText,
    getHomeMatchBasicRegionText,
    getHomeMatchBasicRoomsText,
    getHomeMatchBasicSummaryCopy
} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowPreferenceRow} from "./HomeMatchBasicPreferencesFlowPreferenceRow";

interface IProps {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    values: IPreferencesDefaultValues;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
    trackingContext?: IHomeMatchBasicPreferencesFlowTrackingContext;
    overlayVariant?: IModalOverlayVariant;
}

export const HomeMatchBasicPreferencesFlowSummaryModal = (props: IProps) => {
    const {
        isOpen,
        source,
        values,
        regionDetails,
        homeMatchBasicConsent,
        isSaving,
        onHomeMatchBasicConsentChange,
        onEditPreferences,
        onConfirm,
        onClose,
        trackingContext,
        overlayVariant
    } = props;
    const {title, description, buttonText} = getHomeMatchBasicSummaryCopy(source);
    const wasViewTracked = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            wasViewTracked.current = false;
            return;
        }

        if (trackingContext && !wasViewTracked.current) {
            wasViewTracked.current = true;
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "view",
                homeMatchBasicConsent
            });
        }
    }, [homeMatchBasicConsent, isOpen, trackingContext]);

    const handleClose = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "close",
                homeMatchBasicConsent
            });
        }

        onClose();
    };

    const handleConfirm = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "submit",
                homeMatchBasicConsent
            });
        }

        onConfirm();
    };

    const handleEditPreferences = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowEditPreferences(trackingContext);
        }

        onEditPreferences();
    };

    const handleHomeMatchBasicConsentChange = (value: boolean) => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowToggle({
                ...trackingContext,
                isEnabled: value
            });
        }

        onHomeMatchBasicConsentChange(value);
    };

    return (
        <Modal isOpen={isOpen} className={modalStyle} onModalClose={handleClose} overlayVariant={overlayVariant}>
            <Modal.CloseButton placement="right" variant="rounded" className={closeButtonStyle} />
            <Modal.Content className={summaryContentStyle}>
                <div className={headingWrapperStyle}>
                    <Text variant="headline_3" as="h2">
                        <Highlight>{title}</Highlight>
                    </Text>
                    <Text variant="body_copy_2" color="var(--colors-gray-800)">
                        {description}
                    </Text>
                </div>

                <section className={preferencesCardStyle}>
                    <div className={preferencesCardLeftColumnStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            Twoje preferencje
                        </Text>

                        <div className={preferencesListStyle}>
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={MapMarkerOutlineIcon} text={getHomeMatchBasicRegionText(values, regionDetails)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={RoomsIcon} text={getHomeMatchBasicRoomsText(values)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={PriceIcon} text={getHomeMatchBasicBudgetText(values)} />
                        </div>
                    </div>

                    <div className={preferencesCardRightColumnStyle}>
                        <Button variant="outlined_secondary" size="small" iconLeft={EditIcon} className={editButtonStyle} onClick={handleEditPreferences}>
                            ZMIEŃ
                        </Button>
                    </div>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={homeMatchTextStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            <span id="home_match_basic_consent_label">Dopasujemy oferty dla Ciebie</span>
                        </Text>
                        <Text variant="body_copy_2" color="var(--colors-gray-700)">
                            Włącz tę opcję, a dzięki niej:
                        </Text>
                        <ul className={homeMatchListStyle}>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Automatycznie wyślemy zapytania do deweloperów w Twoim imieniu.
                                </Text>
                            </li>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Deweloperzy wrócą do Ciebie z dopasowanymi ofertami.
                                </Text>
                            </li>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Poinformujemy Cię o najlepszych rekomendacjach od razu SMS-em i e-mailem.
                                </Text>
                            </li>
                        </ul>
                    </div>
                    <Switcher
                        id="home_match_basic_consent"
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-success)"
                        onChange={handleHomeMatchBasicConsentChange}
                        aria-labelledby="home_match_basic_consent_label"
                    />
                </section>

                <div className={mobileStickyActionBarStyle}>
                    <Button variant="filled_primary" size="medium" className={primaryButtonStyle} onClick={handleConfirm} disabled={isSaving}>
                        {isSaving ? "ZAPISYWANIE..." : buttonText}
                    </Button>
                </div>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = css({
    sm: {
        width: "544px",
        borderRadius: 2
    }
});

const closeButtonStyle = css({
    m: 1.5,
    height: "3.6rem",
    width: "3.6rem"
});

const summaryContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 1.5,
    pb: 11,

    sm: {
        gap: 3,
        p: 3
    }
});

const headingWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const preferencesCardStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 2,
    gap: 2
});

const preferencesCardLeftColumnStyle = css({
    flex: "1 1 auto"
});

const preferencesCardRightColumnStyle = css({
    flex: "0 0"
});

const editButtonStyle = css({
    flexShrink: 0
});

const preferencesListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    pt: 2
});

const homeMatchCardStyle = css({
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 1.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const homeMatchTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const homeMatchListStyle = css({
    listStyle: "none",
    m: 0,
    p: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const homeMatchListItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1
});

const primaryButtonStyle = css({
    width: "100%"
});

const mobileStickyActionBarStyle = css({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    p: 1.5,
    backgroundColor: "var(--colors-white)",
    zIndex: 10,

    sm: {
        position: "static",
        p: 0,
        pt: 1
    }
});
import {useEffect, useRef} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CheckIcon, EditIcon, MapMarkerOutlineIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Switcher} from "@pg-design/switcher-module";
import {Highlight, Text} from "@pg-design/text-module";

import type {IRegionListRegion} from "../../../search/actions/fetch_search_regions_action";
import {
    hitGtmHomeMatchBasicFlowEditPreferences,
    hitGtmHomeMatchBasicFlowModal,
    hitGtmHomeMatchBasicFlowToggle
} from "../../tracking/home_match_basic_flow_gtm_events";
import type {HomeMatchBasicPreferencesFlowSource, IHomeMatchBasicPreferencesFlowTrackingContext} from "../../types/HomeMatchBasicPreferencesFlowTypes";
import type {IPreferencesDefaultValues} from "../../types/IPreferencesDefaultValues";
import {
    getHomeMatchBasicBudgetText,
    getHomeMatchBasicRegionText,
    getHomeMatchBasicRoomsText,
    getHomeMatchBasicSummaryCopy
} from "../../utils/home_match_basic_preferences_flow_utils";
import {HomeMatchBasicPreferencesFlowPreferenceRow} from "./HomeMatchBasicPreferencesFlowPreferenceRow";

interface IProps {
    isOpen: boolean;
    source: HomeMatchBasicPreferencesFlowSource;
    values: IPreferencesDefaultValues;
    regionDetails?: IRegionListRegion[];
    homeMatchBasicConsent: boolean;
    isSaving: boolean;
    onHomeMatchBasicConsentChange: (value: boolean) => void;
    onEditPreferences: () => void;
    onConfirm: () => void;
    onClose: () => void;
    trackingContext?: IHomeMatchBasicPreferencesFlowTrackingContext;
    overlayVariant?: IModalOverlayVariant;
}

export const HomeMatchBasicPreferencesFlowSummaryModal = (props: IProps) => {
    const {
        isOpen,
        source,
        values,
        regionDetails,
        homeMatchBasicConsent,
        isSaving,
        onHomeMatchBasicConsentChange,
        onEditPreferences,
        onConfirm,
        onClose,
        trackingContext,
        overlayVariant
    } = props;
    const {title, description, buttonText} = getHomeMatchBasicSummaryCopy(source);
    const wasViewTracked = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            wasViewTracked.current = false;
            return;
        }

        if (trackingContext && !wasViewTracked.current) {
            wasViewTracked.current = true;
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "view",
                homeMatchBasicConsent
            });
        }
    }, [homeMatchBasicConsent, isOpen, trackingContext]);

    const handleClose = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "close",
                homeMatchBasicConsent
            });
        }

        onClose();
    };

    const handleConfirm = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowModal({
                ...trackingContext,
                eventAction: "submit",
                homeMatchBasicConsent
            });
        }

        onConfirm();
    };

    const handleEditPreferences = () => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowEditPreferences(trackingContext);
        }

        onEditPreferences();
    };

    const handleHomeMatchBasicConsentChange = (value: boolean) => {
        if (trackingContext) {
            hitGtmHomeMatchBasicFlowToggle({
                ...trackingContext,
                isEnabled: value
            });
        }

        onHomeMatchBasicConsentChange(value);
    };

    return (
        <Modal isOpen={isOpen} className={modalStyle} onModalClose={handleClose} overlayVariant={overlayVariant}>
            <Modal.CloseButton placement="right" variant="rounded" className={closeButtonStyle} />
            <Modal.Content className={summaryContentStyle}>
                <div className={headingWrapperStyle}>
                    <Text variant="headline_3" as="h2">
                        <Highlight>{title}</Highlight>
                    </Text>
                    <Text variant="body_copy_2" color="var(--colors-gray-800)">
                        {description}
                    </Text>
                </div>

                <section className={preferencesCardStyle}>
                    <div className={preferencesCardLeftColumnStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            Twoje preferencje
                        </Text>

                        <div className={preferencesListStyle}>
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={MapMarkerOutlineIcon} text={getHomeMatchBasicRegionText(values, regionDetails)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={RoomsIcon} text={getHomeMatchBasicRoomsText(values)} />
                            <HomeMatchBasicPreferencesFlowPreferenceRow icon={PriceIcon} text={getHomeMatchBasicBudgetText(values)} />
                        </div>
                    </div>

                    <div className={preferencesCardRightColumnStyle}>
                        <Button variant="outlined_secondary" size="small" iconLeft={EditIcon} className={editButtonStyle} onClick={handleEditPreferences}>
                            ZMIEŃ
                        </Button>
                    </div>
                </section>

                <section className={homeMatchCardStyle}>
                    <div className={homeMatchTextStyle}>
                        <Text variant="headline_6" as="h3" strong>
                            <span id="home_match_basic_consent_label">Dopasujemy oferty dla Ciebie</span>
                        </Text>
                        <Text variant="body_copy_2" color="var(--colors-gray-700)">
                            Włącz tę opcję, a dzięki niej:
                        </Text>
                        <ul className={homeMatchListStyle}>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Automatycznie wyślemy zapytania do deweloperów w Twoim imieniu.
                                </Text>
                            </li>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Deweloperzy wrócą do Ciebie z dopasowanymi ofertami.
                                </Text>
                            </li>
                            <li className={homeMatchListItemStyle}>
                                <CheckIcon size="1.6" wrapperColor="transparent" wrapperSize="2" fill="var(--colors-gray-700)" />
                                <Text variant="body_copy_2" color="var(--colors-gray-700)">
                                    Poinformujemy Cię o najlepszych rekomendacjach od razu SMS-em i e-mailem.
                                </Text>
                            </li>
                        </ul>
                    </div>
                    <Switcher
                        id="home_match_basic_consent"
                        checked={homeMatchBasicConsent}
                        checkedColor="var(--colors-success)"
                        onChange={handleHomeMatchBasicConsentChange}
                        aria-labelledby="home_match_basic_consent_label"
                    />
                </section>

                <div className={mobileStickyActionBarStyle}>
                    <Button variant="filled_primary" size="medium" className={primaryButtonStyle} onClick={handleConfirm} disabled={isSaving}>
                        {isSaving ? "ZAPISYWANIE..." : buttonText}
                    </Button>
                </div>
            </Modal.Content>
        </Modal>
    );
};

const modalStyle = css({
    sm: {
        width: "544px",
        borderRadius: 2
    }
});

const closeButtonStyle = css({
    m: 1.5,
    height: "3.6rem",
    width: "3.6rem"
});

const summaryContentStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    p: 1.5,
    pb: 11,

    sm: {
        gap: 3,
        p: 3
    }
});

const headingWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const preferencesCardStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 2,
    gap: 2
});

const preferencesCardLeftColumnStyle = css({
    flex: "1 1 auto"
});

const preferencesCardRightColumnStyle = css({
    flex: "0 0"
});

const editButtonStyle = css({
    flexShrink: 0
});

const preferencesListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    pt: 2
});

const homeMatchCardStyle = css({
    border: "1px solid var(--colors-gray-300)",
    borderRadius: 2,
    p: 1.5,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2
});

const homeMatchTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const homeMatchListStyle = css({
    listStyle: "none",
    m: 0,
    p: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const homeMatchListItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1
});

const primaryButtonStyle = css({
    width: "100%"
});

const mobileStickyActionBarStyle = css({
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    p: 1.5,
    backgroundColor: "var(--colors-white)",
    zIndex: 10,

    sm: {
        position: "static",
        p: 0,
        pt: 1
    }
});
